import * as React from "react";

import { UrlStateProviderProps } from "../../../Common";

import { PlpSelectedFacetOption, PlpSortingOptions } from "../../model";

import {
  decodeFacetNameFromExtraOptionsConfigurationDataKey,
  decodeFacetNameFromExtraOptionsDataKey,
  EXTRA_FACET_DISPLAY_NAMES_QUERY_KEY,
  EXTRA_OPTIONS_CONFIGURATION_DATA_QUERY_KEY,
  EXTRA_OPTIONS_DATA_QUERY_KEY,
  FacetUrlCache,
  PlpSelectedFacetOptionQueryExtraData,
  PlpState_UrlCached,
  UrlSerializedFacet,
  UrlSerializedFacet_OneItemLength,
} from "../../url-state";

export interface PlpDataSourceUrlStateConsumerInterface {
  readers: UrlStateProviderProps["readers"];
  isAnyKindOfPlpData: (key: string) => boolean;
  isDependentPlpData: (key: string) => boolean;
  plpStateUrlCached: PlpState_UrlCached | null;
}

const defaultValue = {} as unknown as PlpDataSourceUrlStateConsumerInterface;

const PlpDataSourceUrlStateContext = React.createContext(defaultValue);

export const PlpDataSourceUrlStateProvider: React.FunctionComponent = (
  props
) => {
  const [plpStateUrlCached, setPlpStateUrlCached] =
    React.useState<PlpState_UrlCached | null>(null);

  const readPage: UrlStateProviderProps["readers"][number] = React.useCallback(
    (key, value) => {
      const isApplicable = key === "page";
      if (!isApplicable) {
        return;
      }

      const thePageToStartFromInQuery = parseInt(value as string) || 0;
      setPlpStateUrlCached((previous) => ({
        ...(previous || {}),
        ...{
          page: thePageToStartFromInQuery,
          startPage: thePageToStartFromInQuery,
        },
      }));
    },
    []
  );

  const readSort: UrlStateProviderProps["readers"][number] = React.useCallback(
    (key, value) => {
      const isApplicable = key === "sort";
      if (!isApplicable) {
        return;
      }

      const selectedSortingOptionValue = value || "";
      const correspondingSortingOption =
        PlpSortingOptions.find(
          ({ value }) => value === selectedSortingOptionValue
        ) || PlpSortingOptions[0];
      setPlpStateUrlCached((previous) => ({
        ...(previous || {}),
        ...{
          selectedSortingOption: correspondingSortingOption,
        },
      }));
    },
    []
  );

  const readFacets: UrlStateProviderProps["readers"][number] =
    React.useCallback((key, value) => {
      const isApplicable =
        key.startsWith(EXTRA_FACET_DISPLAY_NAMES_QUERY_KEY) &&
        Array.isArray(value);
      if (!isApplicable) {
        return;
      }

      let facets: PlpState_UrlCached["facets"] = {};

      const handleFacet = (urlCachedFacet: UrlSerializedFacet) => {
        const [
          facetUrlCacheItem_Key_Name,
          facetUrlCacheItem_DisplayName,
          facetUrlCacheItem_Id,
          facetUrlCacheItem_ResponseId,
        ] = urlCachedFacet;

        const thisDeserializedFacet = {
          [facetUrlCacheItem_Key_Name]: [
            facetUrlCacheItem_DisplayName,
            facetUrlCacheItem_Id,
            facetUrlCacheItem_ResponseId,
          ] as FacetUrlCache["facets"],
        };

        facets = {
          ...facets,
          ...thisDeserializedFacet,
        };
      };

      let runningData: Partial<UrlSerializedFacet> = [];
      value.forEach((value) => {
        runningData = [
          ...runningData,
          ...[value],
        ] as Partial<UrlSerializedFacet>;
        if (runningData.length === UrlSerializedFacet_OneItemLength) {
          handleFacet(runningData as UrlSerializedFacet);
          runningData = [];
        }
      });

      setPlpStateUrlCached((previous) => ({
        ...(previous || {}),
        ...{
          facets: {
            ...(previous?.facets || {}),
            ...facets,
          },
        },
      }));
    }, []);

  const readOptionsConfiguration: UrlStateProviderProps["readers"][number] =
    React.useCallback(
      (key, value) => {
        const isApplicable = key.startsWith(
          EXTRA_OPTIONS_CONFIGURATION_DATA_QUERY_KEY
        );
        if (!isApplicable) {
          return;
        }

        const thisFacetName =
          decodeFacetNameFromExtraOptionsConfigurationDataKey(key);

        const [isMultiSelect, ...names] =
          value as PlpSelectedFacetOptionQueryExtraData;

        setPlpStateUrlCached((previous) => {
          let nameIndex = 0;
          const selectedOptions: PlpSelectedFacetOption[] = (
            previous?.selectedOptions || []
          ).map((selectedOption) => {
            if (selectedOption.facetName === thisFacetName) {
              const thisSelectedOptionName = names[nameIndex];
              selectedOption = {
                ...selectedOption,
                ...{
                  name: thisSelectedOptionName,
                  facetIsMultiSelect: isMultiSelect,
                },
              };
              nameIndex++;
            }
            return selectedOption;
          });
          return {
            ...(previous || {}),
            ...{
              selectedOptions,
            },
          };
        });
      },
      [plpStateUrlCached]
    );

  const readSelectedOptions: UrlStateProviderProps["readers"][number] =
    React.useCallback((key, value) => {
      const isApplicable = key.startsWith(EXTRA_OPTIONS_DATA_QUERY_KEY);
      if (!isApplicable) {
        return;
      }

      const facetName = decodeFacetNameFromExtraOptionsDataKey(key);

      let values: string[] = [];
      const isValueAnArrayWithoutModification = Array.isArray(value);
      if (isValueAnArrayWithoutModification) {
        values = value;
      } else if (value) {
        values = [value];
      }

      const selectedOptions: PlpSelectedFacetOption[] = values.map(
        (specificOption) => {
          const selectedOption: PlpSelectedFacetOption = {
            facetName,
            id: specificOption,
            facetIsMultiSelect: isValueAnArrayWithoutModification,
          };
          return selectedOption;
        }
      );

      setPlpStateUrlCached((previous) => ({
        ...(previous || {}),
        ...{
          selectedOptions: [
            ...(previous?.selectedOptions || []),
            ...selectedOptions,
          ],
        },
      }));
    }, []);

  const isAnyKindOfPlpData = React.useCallback((key: string) => {
    return (
      key.startsWith(EXTRA_FACET_DISPLAY_NAMES_QUERY_KEY) ||
      key.startsWith(EXTRA_OPTIONS_CONFIGURATION_DATA_QUERY_KEY) ||
      key.startsWith(EXTRA_OPTIONS_DATA_QUERY_KEY)
    );
  }, []);

  const isDependentPlpData = React.useCallback((key: string) => {
    return (
      key.startsWith(EXTRA_FACET_DISPLAY_NAMES_QUERY_KEY) ||
      key.startsWith(EXTRA_OPTIONS_CONFIGURATION_DATA_QUERY_KEY)
    );
  }, []);

  const readers: UrlStateProviderProps["readers"] = React.useMemo(
    () => [
      readPage,
      readSort,
      readFacets,
      readOptionsConfiguration,
      readSelectedOptions,
    ],
    [
      readPage,
      readSort,
      readFacets,
      readOptionsConfiguration,
      readSelectedOptions,
    ]
  );
  const value: PlpDataSourceUrlStateConsumerInterface = React.useMemo(
    () => ({
      plpStateUrlCached,
      readers,
      isAnyKindOfPlpData,
      isDependentPlpData,
    }),
    [plpStateUrlCached, readers, isAnyKindOfPlpData, isDependentPlpData]
  );

  return (
    <PlpDataSourceUrlStateContext.Provider value={value}>
      {props.children || []}
    </PlpDataSourceUrlStateContext.Provider>
  );
};

export const usePlpDataSourceUrlState = () => {
  const context = React.useContext(PlpDataSourceUrlStateContext);
  if (context === defaultValue) {
    throw new Error(
      "usePlpDataSourceUrlState must be used within a PlpDataSourceUrlStateProvider"
    );
  }
  return context;
};
